import React from "react";
import { Check, X, Inbox, ExternalLink, NotebookPen, Link2, Quote, HelpCircle, Sparkles, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { safeUrl } from "@/lib/safeUrl";

const ICONS = { note: NotebookPen, link: Link2, quote: Quote, question: HelpCircle };
const TYPE_LABELS = {
  knowledge_system: "Knowledge System", school: "School", concept: "Concept",
  framework: "Framework", text: "Text", person: "Person",
};

export const ContributionsPanel = ({ items, onResolve, onCreateEntity }) => (
  <div>
    <h2 className="font-serif text-2xl text-foreground mb-1">Learner Contributions</h2>
    <p className="text-sm text-muted-foreground mb-5">
      Notes, links, quotes, questions and new-entity proposals learners suggested for the source material. Accept to acknowledge (then edit/create the entity), or dismiss.
    </p>
    {items.length === 0 ? (
      <div className="bg-card border border-border border-dashed rounded-lg p-10 text-center text-muted-foreground" data-testid="contributions-empty">
        <Inbox className="w-8 h-8 mx-auto mb-3 opacity-40" /> No suggestions waiting.
      </div>
    ) : (
      <div className="space-y-3" data-testid="contributions-list">
        {items.map((c) => {
          const isNew = c.kind === "new_entity";
          const Icon = isNew ? Sparkles : (ICONS[c.entry_type] || NotebookPen);
          const href = safeUrl(c.url);
          return (
            <div key={c.id} className={`bg-card border rounded-lg p-4 ${isNew ? "border-primary/40" : "border-border"}`} data-testid={`contribution-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon className={`w-4 h-4 ${isNew ? "text-primary" : "text-primary"}`} />
                    {isNew ? (
                      <>
                        <Badge className="bg-primary text-primary-foreground">New {TYPE_LABELS[c.proposed_type] || "entity"}</Badge>
                        <span className="font-serif text-lg text-foreground">{c.proposed_name}</span>
                      </>
                    ) : (
                      <>
                        <Badge variant="outline" className="border-accent/40 text-accent capitalize">{c.entry_type}</Badge>
                        {c.context_title && <span className="text-xs text-muted-foreground">on <b className="text-foreground">{c.context_title}</b></span>}
                      </>
                    )}
                  </div>
                  {c.text && <p className="text-sm text-foreground mt-2 whitespace-pre-line">{c.text}</p>}
                  {href && (
                    <a href={href} target="_blank" rel="noreferrer noopener" className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                      <ExternalLink className="w-3.5 h-3.5" /> <span className="truncate">{c.label || href}</span>
                    </a>
                  )}
                  <div className="text-[11px] text-muted-foreground mt-2">from {c.user_name || c.user_email || "a learner"}</div>
                </div>
                <div className="flex items-center gap-2">
                  {isNew && onCreateEntity && (
                    <Button size="sm" variant="outline" className="border-primary/50 text-primary hover:bg-primary/10"
                      onClick={() => onCreateEntity(c)} data-testid={`create-entity-${c.id}`}>
                      <Plus className="w-4 h-4 mr-1" /> Create entity
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => onResolve(c.id, "dismiss")} data-testid={`dismiss-${c.id}`}>
                    <X className="w-4 h-4 mr-1" /> Dismiss
                  </Button>
                  <Button size="sm" onClick={() => onResolve(c.id, "accept")} data-testid={`accept-${c.id}`} className="bg-accent hover:bg-accent/90 text-accent-foreground">
                    <Check className="w-4 h-4 mr-1" /> Accept
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);
